import { useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { adminService } from "../../features/admin/service";
import type { PaginatedResponse } from "../../features/admin/types";
import { formatIST } from "../../utils/dateUtils";
import ConfirmModal from "../../components/admin/ConfirmModal";
import Pagination from "../../components/admin/Pagination";
import { useAlertModal } from "../../components/admin/AlertModal";
import CategoryPromotionToggle from "../../components/admin/CategoryPromotionToggle";
import DateRangeFilter, { type DateFilterEntry } from "../../components/admin/DateRangeFilter";
import Spinner from "../../components/ui/Spinner";
import RefreshButton from "../../components/admin/RefreshButton";
import { useAdminToast } from "../../context/AdminToastContext";

const MAX_UPLOAD_SIZE_BYTES = 500 * 1024 * 1024; // 500 MB — keep in sync with backend MAX_FILE_SIZE_BYTES

const WEBINAR_DATE_FIELDS = [
  { value: "created_at", label: "Created" },
  { value: "updated_at", label: "Updated" },
];

const STATUSES = ["all", "upcoming", "live", "completed", "cancelled", "no_recording", "no_date_time"];
const STATUS_LABELS: Record<string, string> = { no_recording: "No recording", no_date_time: "No date/time" };
const PLATFORMS = ["zoom", "google_meet", "teams", "webex", "goto_webinar", "youtube_live", "whatsapp", "other"];

const EMPTY_FORM = {
  title: "",
  description: "",
  presenter: "",
  webinar_date: "",
  webinar_time: "",
  webinar_link: "",
  platform: "zoom",
  duration_minutes: 60,
  is_free: true,
  price: "",
  topics: "",
  admin_likes_boost: 0,
  admin_saves_boost: 0,
};

const AdminWebinarsPage: React.FC = () => {
  const [data, setData] = useState<PaginatedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilters, setDateFilters] = useState<DateFilterEntry[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const { alertModal, showAlert } = useAlertModal();
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<any | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formFile, setFormFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const { showError } = useAdminToast();
  useEffect(() => { if (formError) showError(formError); }, [formError, showError]);
  const [mediaTarget, setMediaTarget] = useState<any | null>(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [mediaError, setMediaError] = useState("");
  const [previewTarget, setPreviewTarget] = useState<any | null>(null);

  const fetchWebinars = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, page_size: pageSize };
      if (search) params.search = search;
      if (statusFilter !== "all") params.webinar_status = statusFilter;
      const activeDates = dateFilters.filter((d) => d.from || d.to);
      if (activeDates.length) params.date_filters = JSON.stringify(activeDates);
      setData(await adminService.getWebinars(params));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, statusFilter, dateFilters]);

  useEffect(() => { fetchWebinars(); }, [fetchWebinars]);
  useEffect(() => { setPage(1); }, [pageSize]);

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await adminService.deleteWebinar(deleteTarget.id);
      setDeleteTarget(null);
      fetchWebinars();
    } catch (err: any) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      showAlert(typeof detail === "string" ? detail : "Failed to delete webinar.");
    } finally {
      setDeleting(false);
    }
  };

  const openCreateForm = () => {
    setEditTarget(null);
    setForm({ ...EMPTY_FORM });
    setFormFile(null);
    setFormError("");
    setShowForm(true);
  };

  const openEditForm = (w: any) => {
    setEditTarget(w);
    setForm({
      title: w.title || "",
      description: w.description || "",
      presenter: w.presenter || "",
      webinar_date: w.webinar_date || "",
      webinar_time: w.webinar_time || "",
      webinar_link: w.webinar_link || "",
      platform: w.platform || "zoom",
      duration_minutes: w.duration_minutes || 60,
      is_free: w.is_free ?? true,
      price: w.price ? String(w.price) : "",
      topics: (w.topics || []).join(", "),
      admin_likes_boost: w.admin_likes_boost ?? 0,
      admin_saves_boost: w.admin_saves_boost ?? 0,
    });
    setFormFile(null);
    setFormError("");
    setShowForm(true);
  };

  const handleToggleNoRecording = async (w: any) => {
    setCancellingId(w.id);
    try {
      await adminService.updateWebinar(w.id, { no_recording_available: !w.no_recording_available });
      fetchWebinars();
    } catch (err) {
      console.error(err);
    } finally {
      setCancellingId(null);
    }
  };

  const handleCancelWebinar = async (w: any) => {
    setCancellingId(w.id);
    try {
      await adminService.updateWebinar(w.id, { status: "cancelled" });
      fetchWebinars();
    } catch (err) {
      console.error(err);
    } finally {
      setCancellingId(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) { setFormError("Title is required"); return; }
    if (!form.description.trim()) { setFormError("Description is required"); return; }
    if (!form.presenter.trim()) { setFormError("Presenter is required"); return; }
    if (!form.webinar_date) { setFormError("Date is required"); return; }
    if (!form.webinar_time) { setFormError("Time is required"); return; }
    if (!form.webinar_link.trim()) { setFormError("Join link is required"); return; }
    setSubmitting(true);
    setFormError("");
    try {
      const payload: Record<string, any> = {
        title: form.title.trim(),
        description: form.description.trim(),
        presenter: form.presenter.trim(),
        webinar_date: form.webinar_date,
        webinar_time: form.webinar_time,
        webinar_link: form.webinar_link.trim(),
        platform: form.platform,
        duration_minutes: Number(form.duration_minutes) || 60,
        is_free: form.is_free,
        topics: form.topics ? form.topics.split(",").map((t: string) => t.trim()).filter(Boolean) : [],
        tags: [],
        destinations: [],
        admin_likes_boost: Number(form.admin_likes_boost) || 0,
        admin_saves_boost: Number(form.admin_saves_boost) || 0,
      };
      if (!form.is_free && form.price) payload.price = parseFloat(form.price);

      const webinarId = editTarget
        ? (await adminService.updateWebinar(editTarget.id, payload)).id ?? editTarget.id
        : (await adminService.createWebinar(payload)).id;

      if (formFile && webinarId) {
        await adminService.uploadWebinarFile(webinarId, formFile);
      }

      setShowForm(false);
      setEditTarget(null);
      setForm({ ...EMPTY_FORM });
      setFormFile(null);
      fetchWebinars();
    } catch (err: any) {
      setFormError(err?.response?.data?.detail || "Failed to save webinar");
    } finally {
      setSubmitting(false);
    }
  };

  const handleMediaUpload = async (file: File) => {
    if (!mediaTarget) return;
    setUploadingMedia(true);
    setMediaError("");
    try {
      await adminService.uploadWebinarFile(mediaTarget.id, file);
      await fetchWebinars();
      setMediaTarget(null);
    } catch (err: any) {
      setMediaError(err?.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploadingMedia(false);
    }
  };

  const statusColor = (s: string) => {
    const map: Record<string, string> = {
      upcoming: "bg-amber-500/10 text-amber-400",
      live: "bg-green-500/10 text-green-400",
      completed: "bg-gray-500/10 text-gray-400",
      cancelled: "bg-red-500/10 text-red-400",
    };
    return map[s] || "bg-gray-500/10 text-gray-400";
  };

  return (
    <>
    <div>
      <AdminPageHeader
        title="Webinars"
        subtitle="Manage webinars & sessions"
        meta={`${data?.total ?? 0} total`}
        actions={
          <>
            <RefreshButton onRefresh={fetchWebinars} />
            <button
              onClick={openCreateForm}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-medium transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Webinar
            </button>
          </>
        }
      />

      <CategoryPromotionToggle slug="webinars" name="Webinars" />

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <input
          type="text"
          placeholder="Search webinars..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="px-4 py-2.5 bg-gray-900 border border-gray-800 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 w-72"
        />
        <div className="flex gap-2">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setPage(1); }}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === s ? "bg-indigo-500/20 text-indigo-400 border border-indigo-500/30" : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
              }`}
            >
              {STATUS_LABELS[s] ?? s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        <DateRangeFilter
          fields={WEBINAR_DATE_FIELDS}
          value={dateFilters}
          onChange={(v) => { setDateFilters(v); setPage(1); }}
        />
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Media</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Title</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Presenter</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Platform</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Status</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Date</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Attendees</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Views</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Likes</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Saves</th>
                <th className="text-right py-3 px-4 text-gray-500 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} className="py-20 text-center text-gray-500"><Spinner /></td></tr>
              ) : data?.items.length === 0 ? (
                <tr><td colSpan={11} className="py-20 text-center text-gray-500">No webinars found</td></tr>
              ) : (
                data?.items.map((w) => (
                  <tr key={w.id} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                    <td className="py-3 px-4">
                      {w.thumbnail_url ? (
                        <button type="button" onClick={() => setPreviewTarget(w)} className="block">
                          <img src={w.thumbnail_url} alt="" className="w-12 h-12 rounded-lg object-cover border border-gray-800 hover:opacity-80 transition-opacity" />
                        </button>
                      ) : w.video_url ? (
                        <button
                          type="button"
                          onClick={() => setPreviewTarget(w)}
                          className="w-12 h-12 rounded-lg border border-gray-800 bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-gray-700 transition-colors"
                          title="Preview video"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                        </button>
                      ) : (
                        <span className="text-xs text-gray-600">None</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-white font-medium truncate max-w-xs">{w.title || "Untitled"}</p>
                    </td>
                    <td className="py-3 px-4 text-gray-400">{w.presenter || w.host_name || "—"}</td>
                    <td className="py-3 px-4 text-gray-400">{w.platform || "—"}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-md text-xs font-medium ${statusColor(w.status)}`}>
                        {w.status || "—"}
                      </span>
                      {w.missing_schedule && (
                        <span
                          title="This webinar has no date and/or time saved. Edit it and add them."
                          className="ml-2 px-2 py-1 rounded-md text-xs font-medium bg-orange-500/10 text-orange-400"
                        >
                          {w.missing_schedule === "both" ? "No date and time" : w.missing_schedule === "date" ? "No date" : "No time"}
                        </span>
                      )}
                      {w.no_recording_available && (
                        <span
                          title={w.no_recording_auto ? "Marked automatically after the webinar ended with no recording" : "Marked by an admin"}
                          className="ml-2 px-2 py-1 rounded-md text-xs font-medium bg-rose-500/10 text-rose-400"
                        >
                          No recording available
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-gray-500 text-xs">
                      {w.webinar_date ? formatIST(w.webinar_date) : "—"}
                    </td>
                    <td className="py-3 px-4 text-gray-400">{w.attendee_count ?? 0}</td>
                    <td className="py-3 px-4 text-gray-400">{w.view_count ?? 0}</td>
                    <td className="py-3 px-4 text-gray-400">{(w.like_count ?? 0) + (w.admin_likes_boost ?? 0)}</td>
                    <td className="py-3 px-4 text-gray-400">{(w.save_count ?? 0) + (w.admin_saves_boost ?? 0)}</td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      <button onClick={() => openEditForm(w)} className="px-2 py-1 text-xs text-gray-300 hover:bg-gray-700/50 rounded-md transition-colors mr-1">Edit</button>
                      <button onClick={() => { setMediaTarget(w); setMediaError(""); }} className="px-2 py-1 text-xs text-indigo-400 hover:bg-indigo-500/10 rounded-md transition-colors mr-1">Upload media</button>
                      <button onClick={() => handleToggleNoRecording(w)} disabled={cancellingId === w.id} className="px-2 py-1 text-xs text-rose-400 hover:bg-rose-500/10 rounded-md transition-colors mr-1 disabled:opacity-50 disabled:cursor-not-allowed">{w.no_recording_available ? "Clear no-recording" : "Mark no recording"}</button>
                      {w.status !== "cancelled" && w.status !== "completed" && (
                        <button onClick={() => handleCancelWebinar(w)} disabled={cancellingId === w.id} className="px-2 py-1 text-xs text-amber-400 hover:bg-amber-500/10 rounded-md transition-colors mr-1 disabled:opacity-50 disabled:cursor-not-allowed">{cancellingId === w.id ? "Cancelling..." : "Cancel"}</button>
                      )}
                      <button onClick={() => setDeleteTarget(w)} className="px-2 py-1 text-xs text-red-400 hover:bg-red-500/10 rounded-md transition-colors">Delete</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>

    {data && data.total > 0 && (
      <Pagination
        page={page - 1}
        pageSize={pageSize}
        total={data.total}
        onPageChange={(p) => setPage(p + 1)}
        onPageSizeChange={setPageSize}
      />
    )}

    {deleteTarget && (
      <ConfirmModal
        title="Delete Webinar"
        description={`"${deleteTarget.title || "This webinar"}" will be permanently removed.`}
        items={["Delete webinar from database", "Delete all registrations for this webinar"]}
        confirmLabel="Delete Webinar"
        busy={deleting}
        busyLabel="Deleting…"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    )}
    {alertModal}

    {previewTarget && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setPreviewTarget(null)}>
        <div className="max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white font-medium truncate pr-4">{previewTarget.title || "Preview"}</h2>
            <button onClick={() => setPreviewTarget(null)} className="text-gray-300 hover:text-white transition-colors flex-shrink-0">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {previewTarget.video_url ? (
            <video src={previewTarget.video_url} controls autoPlay className="w-full max-h-[80vh] rounded-xl" />
          ) : previewTarget.thumbnail_url ? (
            <img src={previewTarget.thumbnail_url} alt="" className="w-full max-h-[80vh] object-contain rounded-xl" />
          ) : null}
        </div>
      </div>
    )}

    {mediaTarget && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md">
          <div className="flex items-center justify-between p-6 border-b border-gray-800">
            <h2 className="text-lg font-semibold text-white">Upload media</h2>
            <button onClick={() => setMediaTarget(null)} className="text-gray-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-6 space-y-4">
            <p className="text-sm text-gray-400">
              {mediaTarget.title || "This webinar"} — upload a cover image/gif for upcoming webinars, or a recording video once completed.
            </p>
            {mediaError && <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2">{mediaError}</p>}
            {mediaTarget.thumbnail_url && (
              <img src={mediaTarget.thumbnail_url} alt="" className="w-full max-h-48 object-cover rounded-xl border border-gray-800" />
            )}
            {mediaTarget.video_url && (
              <video src={mediaTarget.video_url} controls className="w-full max-h-48 rounded-xl border border-gray-800" />
            )}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
              disabled={uploadingMedia}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleMediaUpload(file);
              }}
              className="w-full text-sm text-gray-400 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:bg-indigo-600 file:text-white file:text-sm hover:file:bg-indigo-500"
            />
            {uploadingMedia && <p className="text-sm text-gray-500">Uploading...</p>}
          </div>
        </div>
      </div>
    )}

    {showForm && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b border-gray-800">
            <h2 className="text-lg font-semibold text-white">{editTarget ? "Edit Webinar" : "Add Webinar"}</h2>
            <button onClick={() => { setShowForm(false); setEditTarget(null); }} className="text-gray-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {formError && <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2">{formError}</p>}

            <div>
              <label className="block text-sm text-gray-400 mb-1.5">Title <span className="text-red-400">*</span></label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Webinar title..."
                className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1.5">Description <span className="text-red-400">*</span></label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                placeholder="What will be covered..."
                className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 resize-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Presenter <span className="text-red-400">*</span></label>
                <input
                  type="text"
                  value={form.presenter}
                  onChange={(e) => setForm({ ...form, presenter: e.target.value })}
                  placeholder="Presenter name"
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Platform</label>
                <select
                  value={form.platform}
                  onChange={(e) => setForm({ ...form, platform: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                >
                  {PLATFORMS.map((p) => <option key={p} value={p}>{p.replace(/_/g, " ")}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Date <span className="text-red-400">*</span></label>
                <input
                  type="date"
                  value={form.webinar_date}
                  onChange={(e) => setForm({ ...form, webinar_date: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Time (24h) <span className="text-red-400">*</span></label>
                <input
                  type="time"
                  value={form.webinar_time}
                  onChange={(e) => setForm({ ...form, webinar_time: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Duration (minutes)</label>
                <input
                  type="number"
                  value={form.duration_minutes}
                  onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })}
                  min={15}
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Topics <span className="text-gray-600">(comma-separated)</span></label>
                <input
                  type="text"
                  value={form.topics}
                  onChange={(e) => setForm({ ...form, topics: e.target.value })}
                  placeholder="e.g. visa, india"
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Like boost (admin)</label>
                {editTarget && (
                  <p className="text-xs text-gray-500 mb-1">
                    Real likes: <span className="text-gray-300">{editTarget.like_count ?? 0}</span>
                    {" → "}Total will be: <span className="text-white font-medium">{(editTarget.like_count ?? 0) + (Number(form.admin_likes_boost) || 0)}</span>
                  </p>
                )}
                <input
                  type="number"
                  value={form.admin_likes_boost}
                  onChange={(e) => setForm({ ...form, admin_likes_boost: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                />
                <p className="text-xs text-gray-600 mt-1">Added on top of real likes shown to users — does not affect real user like data.</p>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Save boost (admin)</label>
                {editTarget && (
                  <p className="text-xs text-gray-500 mb-1">
                    Real saves: <span className="text-gray-300">{editTarget.save_count ?? 0}</span>
                    {" → "}Total will be: <span className="text-white font-medium">{(editTarget.save_count ?? 0) + (Number(form.admin_saves_boost) || 0)}</span>
                  </p>
                )}
                <input
                  type="number"
                  value={form.admin_saves_boost}
                  onChange={(e) => setForm({ ...form, admin_saves_boost: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                />
                <p className="text-xs text-gray-600 mt-1">Added on top of real saves shown to users — does not affect real user save data.</p>
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1.5">Meeting Link <span className="text-red-400">*</span></label>
              <input
                type="url"
                value={form.webinar_link}
                onChange={(e) => setForm({ ...form, webinar_link: e.target.value })}
                placeholder="https://zoom.us/j/..."
                className="w-full px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1.5">
                Cover image/gif (upcoming) or recording video (completed)
              </label>
              {formFile ? (
                formFile.type.startsWith("image/") ? (
                  <img src={URL.createObjectURL(formFile)} alt="" className="w-full max-h-48 object-cover rounded-xl border border-gray-700 mb-2" />
                ) : (
                  <video src={URL.createObjectURL(formFile)} controls className="w-full max-h-48 rounded-xl border border-gray-700 mb-2" />
                )
              ) : editTarget?.thumbnail_url ? (
                <img src={editTarget.thumbnail_url} alt="" className="w-full max-h-48 object-cover rounded-xl border border-gray-700 mb-2" />
              ) : editTarget?.video_url ? (
                <video src={editTarget.video_url} controls className="w-full max-h-48 rounded-xl border border-gray-700 mb-2" />
              ) : null}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  if (file && file.size > MAX_UPLOAD_SIZE_BYTES) {
                    setFormError(`File too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum size is 500 MB.`);
                    e.target.value = "";
                    setFormFile(null);
                    return;
                  }
                  setFormError("");
                  setFormFile(file);
                }}
                className="w-full text-sm text-gray-400 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:bg-indigo-600 file:text-white file:text-sm hover:file:bg-indigo-500"
              />
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_free}
                  onChange={(e) => setForm({ ...form, is_free: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-800 accent-indigo-500"
                />
                <span className="text-sm text-gray-400">Free webinar</span>
              </label>
              {!form.is_free && (
                <input
                  type="number"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  placeholder="Price (INR)"
                  className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 text-sm"
                />
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => { setShowForm(false); setEditTarget(null); }}
                className="flex-1 px-4 py-2.5 bg-gray-800 text-gray-300 rounded-xl text-sm font-medium hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl text-sm font-medium transition-colors"
              >
                {submitting ? "Saving..." : editTarget ? "Save Changes" : "Create Webinar"}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}
    </>
  );
};

export default AdminWebinarsPage;
